import { useCallback, useState, ChangeEvent, UIEvent, useEffect } from "react";

import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";

import { trpc } from "../../helpers/trpc";
import { useTranslate } from "../../hooks/useTranslate";
import Image from "../Image/Image";
import { useDebounce } from "../TextField/useDebounce";
import { UnsplashAttribution } from "../UnsplashAttribution/UnsplashAttribution";

export enum UnsplashOrientation {
  LANDSCAPE = "landscape",
  PORTRAIT = "portrait",
  SQUARISH = "squarish",
}

import { useStyles } from "./useStyles";

import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Search } from "@/icons/outlined";

interface SelectImageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (image: string) => void;
  unsplashOrientation?: UnsplashOrientation;
}

interface UnsplashImage {
  id: string;
  width: number;
  height: number;

  alt_description?: string | null;
  urls: {
    small: string;
    regular: string;
    full: string;
  };
  user: {
    id: string;
    name: string;
    username: string;
  };
  links?: {
    download_location?: string | null;
  };
}

export const SelectImageDialog = ({ isOpen, onClose, onSelectImage, unsplashOrientation }: SelectImageDialogProps) => {
  const { mutateAsync: execTrackDownload } = trpc.trackDownload.useMutation();
  const { t } = useTranslate();
  const classes = useStyles();
  const [search, setSearch] = useState<string | null>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [displayKeyWords, setDisplayKeyWords] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [allImages, setAllImages] = useState<UnsplashImage[]>([]);
  const [total, setTotal] = useState(0);

  // Reset pagination when search parameters change
  useEffect(() => {
    setCurrentPage(1);
    setAllImages([]);
  }, [query, unsplashOrientation]);

  const { setDebouncedValue } = useDebounce({
    onChange: (value: string) => setQuery(value),
    debounced: 300,
  });
  const handleChangeSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDebouncedValue(event.target.value);
      setSearch(event.target.value);
    },
    [setDebouncedValue]
  );

  const selectUnsplashImage = useCallback(
    (image: UnsplashImage) => () => {
      if (image.links?.download_location) {
        // Allows Unsplash to track downloads
        execTrackDownload({ location: image.links.download_location });
      }

      onSelectImage(image.urls.regular);
      onClose();
    },
    [execTrackDownload, onClose, onSelectImage]
  );

  const { data: unsplashData, isLoading } = trpc.searchUnsplashImages.useQuery(
    {
      query,
      orientation: unsplashOrientation,
      page: currentPage,
    },
    {
      enabled: Boolean(query || currentPage === 1), // Enable on search or initial load
      onSuccess: (data) => {
        if (data) {
          if (currentPage === 1) {
            // First page - replace all images
            setAllImages(data.results || []);
            setTotal(data.total || 0);
          } else {
            // Subsequent pages - append to existing images
            setAllImages((prev) => [...prev, ...(data.results || [])]);
          }
        }
      },
    }
  );

  const nextPage = unsplashData?.nextPage;

  const loadMoreImages = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const bottomOffset = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (bottomOffset < 10 && nextPage && !isLoading) {
        setCurrentPage(nextPage);
      }
    },
    [nextPage, isLoading]
  );

  const imagesLength = allImages.length;

  const UnsplashColumn = (images?: UnsplashImage[]) => {
    return (
      <Grid container spacing={1}>
        {images?.map((image) => {
          const maxWidth = 580;
          const ratio = image.width / image.height;
          return (
            <Grid item xs={12} key={image.id}>
              <Image
                alt={image.alt_description || "unsplash-image"}
                width={maxWidth}
                height={maxWidth / ratio}
                src={image.urls.regular}
                className={classes.image}
                onClick={selectUnsplashImage(image)}
              />
              <UnsplashAttribution
                photographerName={image.user.name}
                photographerUsername={image.user.username}
                variant="body2"
                className={classes.imageLegend}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const UnsplashColumnLoader = () => {
    const images = new Array(10).fill(undefined);
    return (
      <Grid container spacing={2}>
        {images?.map((_image: undefined, index: number) => {
          const width = Math.random() * 1000;
          const height = Math.random() * 1000;
          const maxWidth = 580;
          const ratio = width / height;
          return (
            <Grid item xs={12} key={index}>
              <Skeleton variant="rectangular" width={maxWidth} height={maxWidth / ratio} />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const keywords = [
    t("common", "animal"),
    t("common", "colorful"),
    t("common", "food"),
    t("common", "nature"),
    t("common", "environment"),
  ];
  const selectKeyWord = (keyword: string) => () => {
    setQuery(keyword);
    setSearch(keyword);
    setDisplayKeyWords(false);
  };

  return (
    <DashboardDialog
      title={t("common", "selectImage")}
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      hideIcon
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DashboardTextField
            value={search}
            onChange={handleChangeSearch}
            vibe="form"
            autoFocus
            endAdornment={
              <DashboardIcon vibe="tertiary">
                <Search />
              </DashboardIcon>
            }
          />
        </Grid>
        {displayKeyWords && (
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {keywords.map((value, index) => {
                return (
                  <Grid item key={index}>
                    <DashboardChip variant="outlined" label={value} onClick={selectKeyWord(value)} />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Grid container justifyContent="flex-end">
                <DashboardTypography variant="body2" className={classes.neutralVariant70}>
                  {t("common", "results", { total })}
                </DashboardTypography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {!isLoading && imagesLength === 0 && search && (
          <Grid item xs={12}>
            <DashboardTypography variant="body1" vibe="secondary-main">
              {t("common", "noResults")}
            </DashboardTypography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container className={classes.imagesContainer} spacing={1} onScroll={loadMoreImages}>
            <Grid item xs={12}>
              {!isLoading && UnsplashColumn(allImages)}
              {isLoading && UnsplashColumnLoader()}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </DashboardDialog>
  );
};
